import React, { useState, useEffect } from "react";
import apiClient from "../../utils/apiClient";
import toast from "react-hot-toast";

const PolarisTab = ({ searchTerm = "" }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sendType, setSendType] = useState("both");

  // Live Tracking States
  const [isSending, setIsSending] = useState(false);
  const [liveResults, setLiveResults] = useState([]);
  const [showLiveModal, setShowLiveModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get("messages/polaris");
        if (response.data && response.data.status === "success") {
          setData(response.data.data);
        } else {
          setError("Failed to fetch data.");
        }
      } catch (err) {
        setError(err.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const [jobState, setJobState] = useState({ is_running: false, total: 0, completed: 0, selected_phones: [] });

  const fetchJobStatus = async () => {
    try {
      const response = await apiClient.get("messages/job_status");
      if (response.data && response.data.status === "success") {
        if (response.data.type === "polaris") {
          setJobState(response.data);
          if (response.data.is_running && !isSending) {
            setIsSending(true);
            setLiveResults(prev => {
              if (prev.length === 0) {
                // reconstruct from job selected_phones
                return response.data.selected_phones.map(phone => {
                  const userRow = data.find(r => r.Phone === phone);
                  return {
                    phone: phone,
                    name: userRow ? userRow.Name : "Unknown",
                    status: "Sending...",
                    whatsapp_status: "Pending",
                    email_status: "Pending",
                    error: null
                  };
                });
              }
              return prev;
            });
          } else if (!response.data.is_running && isSending) {
            setIsSending(false);
          }
        } else {
          setJobState({ is_running: false, total: 0, completed: 0, selected_phones: [] });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchJobStatus();
    const int = setInterval(fetchJobStatus, 3000);
    return () => clearInterval(int);
  }, [isSending, data]);

  const handleSendMessages = async () => {
    if (selectedUsers.length === 0) return;
    
    // Initialize results array for selected users
    const initialResults = selectedUsers.map(phone => {
      const userRow = data.find(r => r.Phone === phone);
      return {
        phone: phone,
        name: userRow ? userRow.Name : "Unknown",
        status: "Pending",
        whatsapp_status: "Pending",
        email_status: "Pending",
        error: null
      };
    });
    setLiveResults([...initialResults]);
    
    try {
      const response = await apiClient.post("messages/polaris/send", { 
        phones: selectedUsers,
        send_type: sendType
      });
      if (response.data.status === "queued") {
        toast.success(response.data.message);
        setShowLiveModal(true);
        fetchJobStatus();
      } else {
        toast.error(response.data.message || "Failed to start job");
      }
    } catch (err) {
      toast.error(err.message || "Network Error");
    }
  };

  // Poll for delivery status every 5 seconds if the modal is open
  useEffect(() => {
    let interval;
    if (showLiveModal) {
      interval = setInterval(async () => {
        try {
          const response = await apiClient.get("messages/status");
          if (response.data && response.data.status === "success") {
            const dbLogs = response.data.data;
            setLiveResults(prev => prev.map(res => {
              // Find log for this client
              const log = dbLogs.find(l => l.client_phone === res.phone || l.client_phone === `91${res.phone}` || `91${l.client_phone}` === res.phone);
              
              return {
                ...res,
                whatsapp_status: log && log.whatsapp_status && log.whatsapp_status !== "-" ? log.whatsapp_status : res.whatsapp_status,
                email_status: log && log.email_status && log.email_status !== "-" ? log.email_status : res.email_status,
                status: (log && (log.whatsapp_status === "Sent" || log.email_status === "Sent")) ? "Processed" : res.status
              };
            }));
          }
        } catch (err) {
          console.error("Error fetching status", err);
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [showLiveModal, isSending]);

  const filteredData = data.filter((row) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    const nameMatch = row.Name?.toString().toLowerCase().includes(s);
    const phoneMatch = row.Phone?.toString().toLowerCase().includes(s);
    const emailMatch = row.Email?.toString().toLowerCase().includes(s);
    const clientIdMatch = row.client_id?.toString().toLowerCase().includes(s) || row.ClientId?.toString().toLowerCase().includes(s);
    return nameMatch || phoneMatch || emailMatch || clientIdMatch;
  });

  return (
    <div className="bg-[#121316] rounded-md shadow-md p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Polaris Clients</h2>
        <div className="flex gap-4">
          <select 
            className="bg-[#121316] text-white border border-[#2b2d36] rounded-md px-3 py-2 outline-none focus:border-[#07F2C7]"
            value={sendType}
            onChange={(e) => setSendType(e.target.value)}
          >
            <option value="both">Send Both</option>
            <option value="whatsapp">Send WhatsApp Only</option>
            <option value="email">Send Email Only</option>
          </select>
          {jobState.is_running ? (
            <button 
              className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold py-2 px-4 rounded-md transition duration-200 animate-pulse"
              onClick={() => setShowLiveModal(true)}
            >
              Progress ({jobState.completed}/{jobState.total})
            </button>
          ) : (
            <button 
              className="bg-[#07F2C7] hover:bg-[#06c9a5] text-black font-semibold py-2 px-4 rounded-md transition duration-200"
              onClick={handleSendMessages}
              disabled={selectedUsers.length === 0}
            >
              Send Messages ({selectedUsers.length})
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex justify-center items-center text-white">Loading...</div>
      ) : error ? (
        <div className="flex-1 flex justify-center items-center text-red-500">{error}</div>
      ) : (
        <div className="flex-1 overflow-auto rounded-md border border-[#2b2d36]">
          <table className="min-w-full text-sm text-left text-gray-300">
            <thead className="text-xs text-gray-400 uppercase bg-[#1e2025] sticky top-0">
              <tr>
                <th className="px-6 py-3 border-b border-[#2b2d36] w-10">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded bg-[#121316] border-gray-600 focus:ring-[#07F2C7] accent-[#07F2C7]"
                    checked={filteredData.length > 0 && selectedUsers.length === filteredData.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(filteredData.map((row) => row.Phone));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 border-b border-[#2b2d36]">Name</th>
                <th className="px-6 py-3 border-b border-[#2b2d36]">Email</th>
                <th className="px-6 py-3 border-b border-[#2b2d36]">Phone</th>
                <th className="px-6 py-3 border-b border-[#2b2d36]">Portfolio Value</th>
                <th className="px-6 py-3 border-b border-[#2b2d36]">Overall Return</th>
                <th className="px-6 py-3 border-b border-[#2b2d36]">Invested Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, idx) => (
                <tr key={idx} className="bg-[#121316] hover:bg-[#1e2025] transition duration-150">
                  <td className="px-6 py-4 border-b border-[#2b2d36]">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded bg-[#121316] border-gray-600 focus:ring-[#07F2C7] accent-[#07F2C7]"
                      checked={selectedUsers.includes(row.Phone)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, row.Phone]);
                        } else {
                          setSelectedUsers(selectedUsers.filter((phone) => phone !== row.Phone));
                        }
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 border-b border-[#2b2d36] whitespace-nowrap">{row["Name"]}</td>
                  <td className="px-6 py-4 border-b border-[#2b2d36] whitespace-nowrap">{row["Email"]}</td>
                  <td className="px-6 py-4 border-b border-[#2b2d36] whitespace-nowrap">{row["Phone"]}</td>
                  <td className="px-6 py-4 border-b border-[#2b2d36] whitespace-nowrap">{row["Portfolio_value"]}</td>
                  <td className="px-6 py-4 border-b border-[#2b2d36] whitespace-nowrap">{row["Overall_return"]}</td>
                  <td className="px-6 py-4 border-b border-[#2b2d36] whitespace-nowrap">{row["Invested_value"]}</td>
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center border-b border-[#2b2d36]">No data found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showLiveModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-50 p-4">
          <div className="bg-[#121316] rounded-md shadow-lg w-full max-w-5xl max-h-[85vh] flex flex-col border border-[#2b2d36]">
            <div className="p-4 border-b border-[#2b2d36] flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">
                {jobState.is_running ? "Sending Messages..." : "Delivery Results"}
              </h3>
              <button 
                onClick={() => setShowLiveModal(false)} 
                className="text-gray-400 hover:text-white transition-colors"
                
              >
                {jobState.is_running ? "Close (Runs in background)" : "Close"}
              </button>
            </div>
            
            <div className="p-4 overflow-auto flex-1">
              <table className="min-w-full text-sm text-left text-gray-300">
                <thead className="text-xs text-gray-400 uppercase bg-[#1e2025]">
                  <tr>
                    <th className="px-4 py-2 border-b border-[#2b2d36]">Name</th>
                    <th className="px-4 py-2 border-b border-[#2b2d36]">Phone</th>
                    <th className="px-4 py-2 border-b border-[#2b2d36]">Overall Status</th>
                    <th className="px-4 py-2 border-b border-[#2b2d36]">WhatsApp</th>
                    <th className="px-4 py-2 border-b border-[#2b2d36]">Email</th>
                    <th className="px-4 py-2 border-b border-[#2b2d36]">Error Info</th>
                  </tr>
                </thead>
                <tbody>
                  {liveResults.map((r, idx) => (
                    <tr key={idx} className="bg-[#121316] border-b border-[#2b2d36]">
                      <td className="px-4 py-3 font-semibold">{r.name}</td>
                      <td className="px-4 py-3">{r.phone}</td>
                      <td className="px-4 py-3">
                        {r.status === "Pending" && <span className="text-yellow-500">⏳ Pending</span>}
                        {r.status === "Sending..." && <span className="text-blue-500 animate-pulse">🚀 Sending...</span>}
                        {r.status === "Processed" && <span className="text-green-500">✓ Sent</span>}
                        {r.status === "Failed" && <span className="text-red-500">✖ Failed</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${
                          r.whatsapp_status.toLowerCase() === 'delivered' ? 'text-green-400' :
                          r.whatsapp_status === 'Sent' ? 'text-yellow-400' :
                          r.whatsapp_status === 'Failed' ? 'text-red-500' : 'text-gray-500'
                        }`}>
                          {r.whatsapp_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${
                          r.email_status.toLowerCase() === 'delivered' ? 'text-green-400' :
                          r.email_status === 'Sent' ? 'text-yellow-400' :
                          r.email_status === 'Failed' ? 'text-red-500' : 'text-gray-500'
                        }`}>
                          {r.email_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-red-400 text-xs truncate max-w-xs" title={r.error}>
                        {r.error || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!isSending && (
              <div className="p-3 border-t border-[#2b2d36] text-xs text-gray-500 flex justify-between items-center">
                <span>Polling for delivery updates automatically...</span>
                <span>Webhook statuses will update live in the table.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolarisTab;
